import React, { useState } from "react";
import { Info, ArrowRight, ArrowLeft, AlertTriangle, UserCircle2, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getExplanation } from "@/lib/basProcess/explanations";
import { getHowTo } from "@/lib/basProcess/howTo";
import { PROCESS_STEPS } from "@/lib/basProcess/constants";

const docLabel = (doc: string) => {
  const step = PROCESS_STEPS.find((s) => s.docNumber === doc);
  return step ? `Dok ${doc} – ${step.title}` : `Dok ${doc}`;
};

const ChainList: React.FC<{ items: string[]; emptyText: string }> = ({ items, emptyText }) =>
  items.length === 0 ? (
    <p className="text-sm text-muted-foreground">{emptyText}</p>
  ) : (
    <ul className="space-y-1">
      {items.map((d) => (
        <li key={d} className="text-sm text-foreground/90">
          {docLabel(d)}
        </li>
      ))}
    </ul>
  );

interface Props {
  docNumber?: string;
  /** button = knapp, inline = textlänk, panel = alltid utfälld */
  variant?: "button" | "inline" | "panel";
  className?: string;
}

/**
 * Förklarar varför ett dokument behövs och hur det hänger ihop
 * med resten av Bas-processen.
 */
export const DocumentExplanation: React.FC<Props> = ({
  docNumber,
  variant = "button",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const exp = getExplanation(docNumber);
  const howTo = getHowTo(docNumber);
  if (!exp || !docNumber) return null;

  const body = (
    <div className="space-y-5">
      <section className="space-y-1.5">
        <h3 className="text-sm font-semibold">Varför behövs det här?</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{exp.why}</p>
        {exp.legal && exp.legal.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {exp.legal.map((l) => (
              <Badge key={l} variant="secondary" className="text-[10px]">
                {l}
              </Badge>
            ))}
          </div>
        )}
      </section>

      {howTo && (
        <section className="space-y-2 rounded-xl border border-primary/25 bg-primary/5 p-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <ListChecks className="h-4 w-4 text-primary" /> Så här gör du
          </h3>
          <ol className="space-y-1.5">
            {howTo.how.map((step, i) => (
              <li key={step} className="flex gap-2 text-sm text-foreground/90">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
          <div className="grid gap-2 pt-1 sm:grid-cols-2">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Du behöver först: </span>
              {howTo.needs}
            </p>
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Klart när: </span>
              {howTo.doneWhen}
            </p>
          </div>
        </section>
      )}


      <div className="grid gap-4 sm:grid-cols-2">
        <section className="space-y-2 rounded-xl border border-border/60 bg-muted/30 p-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <ArrowLeft className="h-4 w-4 text-muted-foreground" /> Bygger på
          </h3>
          <ChainList items={exp.buildsOn} emptyText="Startpunkt – hämtar inget underlag." />
        </section>
        <section className="space-y-2 rounded-xl border border-border/60 bg-muted/30 p-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <ArrowRight className="h-4 w-4 text-muted-foreground" /> Går vidare till
          </h3>
          <ChainList items={exp.feeds} emptyText="Slutpunkt – informationen arkiveras här." />
        </section>
      </div>

      <section className="flex items-start gap-2 rounded-xl border border-border/60 p-3">
        <UserCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-sm">
          <span className="font-semibold">Ansvarig: </span>
          {exp.owner}
        </p>
      </section>

      <section className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <p className="text-sm">
          <span className="font-semibold">Om du hoppar över det: </span>
          {exp.ifSkipped}
        </p>
      </section>
    </div>
  );

  if (variant === "panel") {
    return <div className={className}>{body}</div>;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === "inline" ? (
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 text-left text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline ${className}`}
          >
            <Info className="h-4 w-4 shrink-0" />
            Varför behövs det här dokumentet och hur hänger det ihop?
          </button>
        ) : (
          <Button variant="ghost" size="sm" className={`gap-2 ${className}`} aria-label="Förklaring">
            <Info className="h-4 w-4" />
            <span className="hidden sm:inline">Förklaring</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{docLabel(docNumber.split("/")[0].trim())}</DialogTitle>
          <DialogDescription>Så hänger dokumentet ihop med resten av processen.</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentExplanation;
